import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  interpolateColor,
} from "react-native-reanimated";

interface ItemProps {
  title: string;
  icon?: React.ReactNode;
  isSelected?: string;
  onPress: (title: string) => void;
  colors?: {
    default: string;
    hover: string;
    selected: string;
  };
}

export default function Item(props: ItemProps) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const backgroundColor = useSharedValue(0);

  useEffect(() => {
    if (props.title === props.isSelected) {
      setIsPressed(true);
      backgroundColor.value = withTiming(1, { duration: 400 });
    } else {
      setIsPressed(false);
      backgroundColor.value = withTiming(0, { duration: 50 });
    }
  }, [props.isSelected]);

  const handleSelect = () => {
    props.onPress(props.title);
  };

  const getColor = () => {
    if (isHovered && isPressed) {
      return props.colors?.selected || "#FFFFFF";
    } else if (isHovered) {
      return props.colors?.hover || "#5D0CBA";
    } else if (isPressed) {
      return props.colors?.selected || "#FFFFFF";
    } else {
      return props.colors?.default || "#919191";
    }
  };

  const animatedStyle = useAnimatedStyle(() => {
    const backgroundColorInterpolated = interpolateColor(
      backgroundColor.value,
      [0, 1],
      ["#FFFFFF", "#5D0CBA"],
    );

    return {
      backgroundColor: backgroundColorInterpolated,
    };
  });

  const getSize = (icon: string) => {
    switch (icon) {
      case t("home/home:option.home"):
        return 18;
      case t("home/home:option.exchange"):
        return 17;
      case t("home/home:option.beneficiaries"):
        return 17;
      case t("management/home:menu.customers"):
        return 18;
      case t("management/home:menu.transactions"):
        return 20;
      case t("management/home:menu.counterparties"):
        return 17;
      case t("management/home:menu.applications"):
        return 20;
      default:
        return 18;
    }
  };

  return (
    <Pressable
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={handleSelect}
    >
      <Animated.View
        style={[animatedStyle]}
        className="flex-row items-center justify-around rounded-full w-36 h-fit py-1.5 px-4 my-2.5"
      >
        <View className="items-center w-1/4 px-0.5">
          {React.cloneElement(props.icon as React.ReactElement, {
            color: getColor(),
            size: getSize(props.title),
          })}
        </View>
        <View className="items-start justify-center w-3/4 ml-1.5">
          <Text
            className={`text-xs font-normal ${
              isHovered && isPressed ? "text-white1" : ""
            } ${isHovered ? "text-purple1" : "text-darkergray"} ${
              isPressed ? "text-white1" : ""
            }`}
          >
            {props.title}
          </Text>
        </View>
      </Animated.View>
    </Pressable>
  );
}
